/**
 * UpgradeDialogHost — the App-mounted host that turns the billing-upgrade
 * axios interceptor's 402 signal into UI. Mounts once near the app root
 * (inside the authenticated tree, alongside <AgeConsentGate>); subscribes to
 * billingUpgradeController and renders <UpgradeDialog> when a gated request
 * fires. Mirrors coppa/AgeConsentGate.js's shape (that host closes each of its
 * overlays itself and has no hide() round-trip; this one keeps its own) — see
 * that file's header for the fuller "why a controller, not context" rationale.
 *
 * Renders NOTHING until a 402 fires, so it is invisible (and zero-cost
 * visually) for the common case (a student under their free allowance, or
 * one who already holds premium).
 *
 * THE DARK GUARD IS `status === 'disabled'`, DELIBERATELY NOT `!enabled`
 * (review SMALLS): a 402 can ONLY be raised while billing_enabled=True on
 * the backend (the dark-semantics contract: no endpoint ever returns 402
 * while billing is dark) — so by the time this host is asked to show a
 * dialog, billing IS enabled, even if useBilling()'s OWN entitlement fetch
 * hasn't resolved yet (`status === 'loading'`, under which `enabled` is
 * ALSO false — see BillingContext.js). Guarding on `!enabled` would
 * suppress a legitimate, already-proven-real 402 during that loading
 * window (e.g. a gated request that resolves before BillingProvider's own
 * mount fetch does); guarding on the CONFIRMED-disabled state only
 * suppresses the dialog when we know for certain it should never have
 * fired at all. The SAME reasoning covers `status === 'error'` (F14-PR3.2's
 * transient-failure state, review S4) without needing its own branch: a
 * real 402 is equally definitive proof billing is on regardless of whether
 * THIS component's own background entitlement re-check happens to be
 * failing transiently at that exact moment — so 'error' falls through to
 * the same "still show it" behavior as 'loading', by simply not being
 * 'disabled'.
 */
import { useEffect, useState } from 'react';
import { subscribe, hide as controllerHide } from './billingUpgradeController';
import { useBilling } from './BillingContext';
import UpgradeDialog from './UpgradeDialog';

export default function UpgradeDialogHost() {
  const billing = useBilling();
  const [details, setDetails] = useState(null);

  useEffect(() => {
    const unsubscribe = subscribe({
      onShow: (d) => setDetails(d),
      onHide: () => setDetails(null),
    });
    return unsubscribe;
  }, []);

  const handleClose = () => {
    setDetails(null);
    controllerHide();
  };

  if (!details) return null;
  if (billing.status === 'disabled') return null;

  return <UpgradeDialog details={details} onClose={handleClose} />;
}
